import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../axiosBaseQuery';
import managementPeriodApiParser from './managementPeriodsApiParser';

// ---------- Types ----------
export interface ManagementPeriod {
  id: string | number;
  vesselName: string;
  manager: string;
  dateRange: string;
  status: 'Active' | 'Inactive';
}

export interface ManagementPeriodsListResponse {
  data: ManagementPeriod[];
}

export interface ActiveManagementPeriod {
  id: number;
  vesselId: number;
  vesselName: string;
  managerId: number;
  managerName: string;
  managerCode: string;
  managementTypeId: number;
  managementTypeName: string;
  dateFrom: string;
  dateTo: string;
  activeForLubricantsApplication: boolean;
  status: string;
  canEdit: boolean;
  canActivate: boolean;
  canDeactivate: boolean;
}

interface UpdateManagementPeriodPayload {
  id: number;
  vesselId: number;
  active: boolean;
}

// ---------- API Slice (merged) ----------
const managementPeriodApi = createApi({
  reducerPath: 'managementPeriodApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['ManagementPeriod'],
  endpoints: (builder) => ({
    getManagementPeriodsList: builder.query<ManagementPeriod[], any>({
      query: () => {
        return {
          url: '/management-periods',
          method: 'GET',
        };
      },
      transformResponse: (res: any): ManagementPeriod[] =>
        managementPeriodApiParser.ManagementPeriodsListParser(res),
    }),

    // Mutations for management period CRUD
    updateManagementPeriod: builder.mutation<any, UpdateManagementPeriodPayload>({
      query: ({ id, vesselId, active }) => ({
        url: `/management-periods/${encodeURIComponent(id)}/vessel/${encodeURIComponent(vesselId)}/status`,
        method: 'PATCH',
        data: { active },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ManagementPeriod' as const, id: id.toString() },
        'ManagementPeriod',
      ],
    }),
  }),
});

// ---------- Reducer, Middleware, Path ----------
export const managementPeriodReducer = managementPeriodApi.reducer;
export const managementPeriodMiddleware = managementPeriodApi.middleware;
export const managementPeriodReducerPath = managementPeriodApi.reducerPath;

// ---------- Hooks / Actions ----------
export const { useGetManagementPeriodsListQuery, useUpdateManagementPeriodMutation } =
  managementPeriodApi;
